import React, { useRef, useState } from "react";
import IngredientFields, { ingredientPayload } from "./IngredientFields";
import { apiErrorMessage } from "./apiError";

const identity = value => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

export default function PurchaseMasterCreate({ api, type, initialName, departmentId, onCreated, onCancel, externalError }) {
  const consumable = type === "consumable", equipment = type === "equipment";
  const [busy, setBusy] = useState(false), [error, setError] = useState(""), [existing, setExisting] = useState(null);
  const lock = useRef(false);
  const submit = async event => {
    event.preventDefault();
    if (lock.current) return;
    const fields = new FormData(event.currentTarget);
    const name = String(fields.get(consumable || equipment ? "name" : "ingredient_name") || "").trim().replace(/\s+/g, " ");
    if (!name) { setError("Enter a name."); return; }
    lock.current = true; setBusy(true); setError(""); setExisting(null);
    try {
      const url = equipment ? "/equipment-vessels" : consumable ? "/operating-inventory/items" : "/ingredients";
      const { data: rows } = await api.get(`${url}?include_inactive=true${consumable || equipment ? "" : "&purchase_request=true"}`);
      const match = rows.find(row => identity(row.name || row.ingredient_name) === identity(name));
      if (match) {
        setExisting(match.active === false ? null : match);
        setError(match.active === false ? `${name} exists but is inactive. Review it in ${equipment ? "Settings → Equipment & Vessels" : consumable ? "Consumables" : "Ingredients"}; no item was created.` : `${name} already exists.`);
        return;
      }
      const payload = equipment ? {name,equipment_type:String(fields.get("equipment_type")),aliases:[],active:true} : consumable ? {
        name, base_tracking_unit: String(fields.get("base_tracking_unit")).trim(),
        base_units_per_purchase_pack: Number(fields.get("base_units_per_purchase_pack")),
        purchase_pack_unit: String(fields.get("purchase_pack_unit")).trim(),
        department_id: departmentId || null,
      } : { ...ingredientPayload(fields), ingredient_name: name };
      const { data } = await api.post(url, payload);
      if (!data.id) throw new Error("The saved item did not return an identifier. Search again before retrying.");
      await onCreated(data);
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(detail?.error || apiErrorMessage(failure, "Could not save item. Search again before retrying."));
      if (detail?.existing_id && detail.active !== false) {
        try {
          const { data } = await api.get(equipment ? "/equipment-vessels" : consumable ? "/operating-inventory/items?purchase_request=true" : "/ingredients?purchase_request=true");
          setExisting(data.find(row => row.id === detail.existing_id) || null);
        } catch { /* Keep the original recoverable error; never invent a selection. */ }
      }
    } finally { lock.current = false; setBusy(false); }
  };
  return <section className="panel" role="region" aria-label={`Create new ${type}`}>
    <h3>Create new {type}</h3>
    <form className="form-grid" onSubmit={submit}>
      {equipment ? <><label>Name<input name="name" defaultValue={initialName} required maxLength={200}/></label><label>Equipment kind<select name="equipment_type" defaultValue="TOOL"><option value="TOOL">Tool</option><option value="APPLIANCE">Appliance</option><option value="VESSEL">Vessel</option></select></label></> : consumable ? <>
        <label>Name<input name="name" defaultValue={initialName} required maxLength={200}/></label>
        <label>Base unit<input name="base_tracking_unit" required placeholder="e.g. ml, kg, piece"/></label>
        <label>Purchase pack unit<input name="purchase_pack_unit" required defaultValue="pack"/></label>
        <label>Base units per pack<input name="base_units_per_purchase_pack" type="number" min="0.0001" step="any" required/></label>
      </> : <IngredientFields compact ingredient={{ ingredient_name: initialName }} categories={[{ name: "Other" }]}/>} 
      {!consumable && !equipment && <small className="full">The Ingredient master requires an actual initial purchase price. Saving records that initial price; no earlier prices are created.</small>}
      {(error||externalError) && <div className="full" role="alert">{error||externalError}{existing && <button type="button" className="text-btn" onClick={() => onCreated(existing)}>Use existing item</button>}</div>}
      <div className="form-actions full"><button type="button" className="secondary-btn" disabled={busy} onClick={onCancel}>Cancel creation</button><button type="submit" className="primary-btn" disabled={busy}>{busy ? "Saving…" : "Save item"}</button></div>
    </form>
  </section>;
}
